import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowUpward,
  ArrowDownward,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { api } from "../services/api";
import { GuestTypeBadge } from "./GuestTypeBadge";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Guest {
  username: string;
  guests: {
    id: number;
    name: string;
    isAttending: number | null;
    foodPreferences: string;
    guestType: "adult" | "child" | "infant";
  }[];
}

interface Gift {
  id?: number;
  title: string;
  description: string;
  totalQuantity: number;
  isActive: boolean;
}

type PendingDelete =
  | {
      kind: "guest";
      id: number;
      name: string;
      groupName: string;
    }
  | {
      kind: "user";
      id: number;
      username: string;
      guestCount: number;
    }
  | { kind: "gift"; id: number; title: string };

interface GiftDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (gift: Gift) => void;
  gift?: Gift;
  title: string;
}

const GiftDialog: React.FC<GiftDialogProps> = ({
  open,
  onClose,
  onSave,
  gift,
  title,
}) => {
  const [giftData, setGiftData] = useState<Gift>(
    gift || {
      title: "",
      description: "",
      totalQuantity: 1,
      isActive: true,
    },
  );
  const [errors, setErrors] = useState({
    title: false,
    description: false,
  });

  useEffect(() => {
    if (gift) {
      setGiftData(gift);
      setErrors({ title: false, description: false });
    } else {
      setGiftData({
        title: "",
        description: "",
        totalQuantity: 1,
        isActive: true,
      });
    }
  }, [gift, open]);

  const handleSave = () => {
    const newErrors = {
      title: !giftData.title.trim(),
      description: !giftData.description.trim(),
    };
    setErrors(newErrors);

    if (
      newErrors.title ||
      newErrors.description ||
      giftData.totalQuantity < 1
    ) {
      return;
    }

    onSave(giftData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Title"
          value={giftData.title}
          onChange={(e) => {
            setGiftData({ ...giftData, title: e.target.value });
            setErrors({ ...errors, title: false });
          }}
          margin="normal"
          error={errors.title}
          helperText={errors.title ? "Title is required" : ""}
          required
        />
        <TextField
          fullWidth
          label="Description"
          value={giftData.description}
          onChange={(e) => {
            setGiftData({ ...giftData, description: e.target.value });
            setErrors({ ...errors, description: false });
          }}
          margin="normal"
          multiline
          rows={4}
          error={errors.description}
          helperText={errors.description ? "Description is required" : ""}
          required
        />
        <TextField
          fullWidth
          label="Quantity"
          type="number"
          value={giftData.totalQuantity}
          onChange={(e) =>
            setGiftData({
              ...giftData,
              totalQuantity: Math.max(1, parseInt(e.target.value) || 1),
            })
          }
          margin="normal"
          inputProps={{ min: 1 }}
          required
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={giftData.isActive}
              onChange={(e) =>
                setGiftData({ ...giftData, isActive: e.target.checked })
              }
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface AddGuestDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    username: string;
    name: string;
    guestType: "adult" | "child" | "infant";
    foodPreferences: string;
  }) => void;
  accounts: { id: number; username: string; guestCount: number }[];
}

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { username: string }) => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState(false);

  useEffect(() => {
    if (open) {
      setUsername("");
      setUsernameError(false);
    }
  }, [open]);

  const handleSave = () => {
    if (!username.trim()) {
      setUsernameError(true);
      return;
    }
    onSave({ username: username.trim() });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add guest login</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This is the name guests use to sign in. It must be unique.
        </Typography>
        <TextField
          fullWidth
          label="Login name"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameError(false);
          }}
          margin="normal"
          error={usernameError}
          helperText={usernameError ? "Login name is required" : ""}
          required
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddGuestDialog: React.FC<AddGuestDialogProps> = ({
  open,
  onClose,
  onSave,
  accounts,
}) => {
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [guestType, setGuestType] = useState<"adult" | "child" | "infant">(
    "adult",
  );
  const [foodPreferences, setFoodPreferences] = useState("");
  const [errors, setErrors] = useState({ username: false, name: false });

  useEffect(() => {
    if (open) {
      setUsername("");
      setName("");
      setGuestType("adult");
      setFoodPreferences("");
      setErrors({ username: false, name: false });
    }
  }, [open]);

  const handleSave = () => {
    const nextErrors = {
      username: !username.trim(),
      name: !name.trim(),
    };
    setErrors(nextErrors);
    if (nextErrors.username || nextErrors.name) return;

    onSave({
      username: username.trim(),
      name: name.trim(),
      guestType,
      foodPreferences: foodPreferences.trim(),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add guest</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal" error={errors.username} required>
          <InputLabel id="add-guest-group-label">Group (login)</InputLabel>
          <Select
            labelId="add-guest-group-label"
            label="Group (login)"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setErrors((prev) => ({ ...prev, username: false }));
            }}
          >
            <MenuItem value="">
              <em>Choose group…</em>
            </MenuItem>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.username}>
                {a.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {errors.username && (
          <Typography variant="caption" color="error" sx={{ display: "block" }}>
            Choose which guest account this person belongs to
          </Typography>
        )}
        {accounts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No guest logins yet. Add a guest login below, then add people to
            that household here.
          </Typography>
        )}
        <TextField
          fullWidth
          label="Guest name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: false }));
          }}
          margin="normal"
          error={errors.name}
          helperText={errors.name ? "Name is required" : ""}
          required
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="add-guest-type-label">Type</InputLabel>
          <Select
            labelId="add-guest-type-label"
            label="Type"
            value={guestType}
            onChange={(e) =>
              setGuestType(e.target.value as "adult" | "child" | "infant")
            }
          >
            <MenuItem value="adult">Adult</MenuItem>
            <MenuItem value="child">Child</MenuItem>
            <MenuItem value="infant">Infant</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Food preferences (optional)"
          value={foodPreferences}
          onChange={(e) => setFoodPreferences(e.target.value)}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={accounts.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AdminPanel: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "username", direction: "asc" });

  // Gift management state
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [openGiftDialog, setOpenGiftDialog] = useState(false);
  const [newGift, setNewGift] = useState<Gift>({
    title: "",
    description: "",
    totalQuantity: 1,
    isActive: true,
  });
  const [editingGift, setEditingGift] = useState<Gift | null>(null);

  const [openAddGuestDialog, setOpenAddGuestDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [guestAccounts, setGuestAccounts] = useState<
    { id: number; username: string; guestCount: number }[]
  >([]);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  useEffect(() => {
    if (tabValue === 0) {
      fetchGuests();
      fetchGuestAccounts();
    } else {
      fetchGifts();
    }
  }, [tabValue]);

  const fetchGuests = async () => {
    try {
      const data = await api.getAllGuests();
      setGuests(data);
    } catch (err) {
      setError("Failed to fetch guests");
    }
  };

  const fetchGuestAccounts = async () => {
    try {
      const accounts = await api.getAdminUsers();
      setGuestAccounts(accounts);
    } catch {
      setError("Failed to load guest logins");
    }
  };

  const handleAddGuest = async (data: {
    username: string;
    name: string;
    guestType: "adult" | "child" | "infant";
    foodPreferences: string;
  }) => {
    try {
      await api.createGuest({
        username: data.username,
        name: data.name,
        guestType: data.guestType,
        foodPreferences: data.foodPreferences,
      });
      setSuccessMessage("Guest added successfully!");
      fetchGuests();
      fetchGuestAccounts();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add guest. Please try again.",
      );
    }
  };

  const handleDeleteGuest = async (guestId: number) => {
    try {
      await api.deleteGuest(guestId);
      setSuccessMessage("Guest removed successfully!");
      fetchGuests();
      fetchGuestAccounts();
    } catch (err) {
      setError("Failed to remove guest. Please try again.");
      console.error("Error removing guest:", err);
    }
  };

  const handleAddUser = async (data: { username: string }) => {
    try {
      await api.createUser(data);
      setSuccessMessage("Guest login added successfully!");
      fetchGuestAccounts();
      fetchGuests();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add guest login. Please try again.",
      );
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await api.deleteUser(userId);
      setSuccessMessage("Guest login removed successfully!");
      fetchGuestAccounts();
      fetchGuests();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to remove guest login. Please try again.",
      );
    }
  };

  const fetchGifts = async () => {
    try {
      const data = await api.getGifts();
      setGifts(data);
    } catch (err) {
      setError("Failed to fetch gifts");
      console.error("Error fetching gifts:", err);
    }
  };

  const handleAddGift = async (giftData: Gift) => {
    try {
      // Validate the data before sending
      if (
        !giftData.title.trim() ||
        !giftData.description.trim() ||
        giftData.totalQuantity < 1
      ) {
        setError("Please fill in all fields. Quantity must be at least 1.");
        return;
      }

      await api.createGift({
        title: giftData.title.trim(),
        description: giftData.description.trim(),
        totalQuantity: giftData.totalQuantity,
        isActive: giftData.isActive,
      });
      setSuccessMessage("Gift added successfully!");
      setOpenGiftDialog(false);
      setNewGift({
        title: "",
        description: "",
        totalQuantity: 1,
        isActive: true,
      });
      fetchGifts();
    } catch (err) {
      setError("Failed to add gift. Please try again.");
      console.error("Error adding gift:", err);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const sortedGuests = useMemo(() => {
    // First, create a flat list of guests with their group info
    const flattenedGuests = guests.flatMap((group) =>
      group.guests.map((guest) => ({
        groupName: group.username,
        ...guest,
      })),
    );

    // Filter based on search term
    const filteredGuests = flattenedGuests.filter(
      (guest) =>
        guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.groupName.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Sort the guests
    return filteredGuests.sort((a, b) => {
      if (sortConfig.key === "username") {
        return sortConfig.direction === "asc"
          ? a.groupName.localeCompare(b.groupName)
          : b.groupName.localeCompare(a.groupName);
      }
      if (sortConfig.key === "name") {
        return sortConfig.direction === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortConfig.key === "status") {
        const getStatus = (isAttending: number | null) => {
          if (isAttending === null) return 0; // Haven't answered
          return isAttending ? 2 : 1; // 2 for attending, 1 for not attending
        };
        return sortConfig.direction === "asc"
          ? getStatus(a.isAttending) - getStatus(b.isAttending)
          : getStatus(b.isAttending) - getStatus(a.isAttending);
      }
      return 0;
    });
  }, [guests, searchTerm, sortConfig]);

  const guestSummary = useMemo(() => {
    const summary = {
      total: sortedGuests.filter((guest) => guest.isAttending === 1).length,
      byType: {
        adult: 0,
        child: 0,
        infant: 0,
      },
      byAttendance: {
        attending: 0,
        notAttending: 0,
        notAnswered: 0,
      },
      byTypeAndAttendance: {
        adult: { attending: 0, notAttending: 0, notAnswered: 0 },
        child: { attending: 0, notAttending: 0, notAnswered: 0 },
        infant: { attending: 0, notAttending: 0, notAnswered: 0 },
      },
    };

    sortedGuests.forEach((guest) => {
      // Count by type
      summary.byType[guest.guestType]++;

      // Count by attendance
      if (guest.isAttending === 1) {
        summary.byAttendance.attending++;
        summary.byTypeAndAttendance[guest.guestType].attending++;
      } else if (guest.isAttending === 0) {
        summary.byAttendance.notAttending++;
        summary.byTypeAndAttendance[guest.guestType].notAttending++;
      } else {
        summary.byAttendance.notAnswered++;
        summary.byTypeAndAttendance[guest.guestType].notAnswered++;
      }
    });

    return summary;
  }, [sortedGuests]);

  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? <ArrowUpward /> : <ArrowDownward />;
  };

  const renderSortableHeader = (label: string, key: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        "&:hover": {
          "& .MuiIconButton-root": {
            opacity: 1,
          },
        },
      }}
      onClick={() => handleSort(key)}
    >
      {label}
      <IconButton
        size="small"
        sx={{
          opacity: sortConfig.key === key ? 1 : 0.3,
          transition: "opacity 0.2s",
          ml: 0.5,
        }}
      >
        {renderSortIcon(key)}
        {!renderSortIcon(key) && <ArrowUpward sx={{ opacity: 0.3 }} />}
      </IconButton>
    </Box>
  );

  const getAttendanceStatus = (isAttending: number | null) => {
    if (isAttending === null) return "Not answered";
    return isAttending ? "Attending" : "Not attending";
  };

  const handleEditGift = (gift: Gift) => {
    setEditingGift(gift);
  };

  const handleUpdateGift = async (updatedGift: Gift) => {
    try {
      if (!updatedGift.id) return;
      await api.updateGift(updatedGift.id, updatedGift);
      setSuccessMessage("Gift updated successfully!");
      setEditingGift(null);
      fetchGifts();
    } catch (err) {
      setError("Failed to update gift. Please try again.");
      console.error("Error updating gift:", err);
    }
  };

  const handleDeleteGift = async (giftId: number) => {
    try {
      await api.deleteGift(giftId);
      setSuccessMessage("Gift deleted successfully!");
      fetchGifts();
    } catch (err) {
      setError("Failed to delete gift. Please try again.");
      console.error("Error deleting gift:", err);
    }
  };

  const closeDeleteDialog = () => {
    if (!deleteInProgress) setPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteInProgress(true);
    try {
      if (pendingDelete.kind === "guest") {
        await handleDeleteGuest(pendingDelete.id);
      } else if (pendingDelete.kind === "user") {
        await handleDeleteUser(pendingDelete.id);
      } else {
        await handleDeleteGift(pendingDelete.id);
      }
    } finally {
      setDeleteInProgress(false);
      setPendingDelete(null);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 8, px: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
        >
          <Tab label="Guests" />
          <Tab label="Wishlist" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ m: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {/* Guest Summary */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {/* Total Guests */}
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography variant="h4" color="primary">
                  {guestSummary.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Attending Guests
                </Typography>
              </CardContent>
            </Card>

            {/* By Guest Type */}
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  By Type
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <GuestTypeBadge guestType="adult" />
                    <Typography variant="body2" fontWeight="bold">
                      {guestSummary.byType.adult}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <GuestTypeBadge guestType="child" />
                    <Typography variant="body2" fontWeight="bold">
                      {guestSummary.byType.child}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <GuestTypeBadge guestType="infant" />
                    <Typography variant="body2" fontWeight="bold">
                      {guestSummary.byType.infant}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Detailed Breakdown */}
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  Type × Attendance
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  {(["adult", "child", "infant"] as const).map((type) => (
                    <Box
                      key={type}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <GuestTypeBadge guestType={type} size="small" />
                      <Box sx={{ display: "flex", gap: 1, fontSize: "0.7rem" }}>
                        <Typography variant="caption" color="success.main">
                          ✓{guestSummary.byTypeAndAttendance[type].attending}
                        </Typography>
                        <Typography variant="caption" color="error.main">
                          ✗{guestSummary.byTypeAndAttendance[type].notAttending}
                        </Typography>
                        <Typography variant="caption" color="warning.main">
                          ?{guestSummary.byTypeAndAttendance[type].notAnswered}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <TextField
            sx={{ flex: "1 1 240px", minWidth: 200 }}
            placeholder="Search guests or groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAddGuestDialog(true)}
            sx={{ flexShrink: 0 }}
          >
            Add guest
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {renderSortableHeader("Group", "username")}
                </TableCell>
                <TableCell>
                  {renderSortableHeader("Guest Name", "name")}
                </TableCell>
                <TableCell>
                  {renderSortableHeader("Status", "status")}
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Food Preferences</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedGuests.map((guest) => {
                const rowColor = guest.isAttending
                  ? "#EAFFEA"
                  : guest.isAttending === null
                    ? "none"
                    : "#FFEAEA";
                return (
                  <TableRow key={guest.id} sx={{ backgroundColor: rowColor }}>
                    <TableCell>{guest.groupName}</TableCell>
                    <TableCell>{guest.name}</TableCell>
                    <TableCell>
                      {getAttendanceStatus(guest.isAttending)}
                    </TableCell>
                    <TableCell>
                      <GuestTypeBadge guestType={guest.guestType} />
                    </TableCell>
                    <TableCell>{guest.foodPreferences || "-"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setPendingDelete({
                            kind: "guest",
                            id: guest.id,
                            name: guest.name,
                            groupName: guest.groupName,
                          })
                        }
                        aria-label={`Remove ${guest.name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            mt: 4,
            mb: 3,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="guest-logins-content"
            id="guest-logins-header"
          >
            <Typography variant="subtitle1" fontWeight={600} component="span">
              Guest logins
            </Typography>
          </AccordionSummary>
          <AccordionDetails id="guest-logins-content">
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenAddUserDialog(true)}
              >
                Add guest login
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each login is one invited household. Removing a login also removes
              its people and wishlist reservations for that account.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Login name</TableCell>
                    <TableCell align="right">People in RSVP</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {guestAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No guest logins yet. Add one to invite a household.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    guestAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.username}</TableCell>
                        <TableCell align="right">
                          {account.guestCount}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setPendingDelete({
                                kind: "user",
                                id: account.id,
                                username: account.username,
                                guestCount: account.guestCount,
                              })
                            }
                            aria-label={`Remove login ${account.username}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <AddGuestDialog
          open={openAddGuestDialog}
          onClose={() => setOpenAddGuestDialog(false)}
          onSave={handleAddGuest}
          accounts={guestAccounts}
        />

        <AddUserDialog
          open={openAddUserDialog}
          onClose={() => setOpenAddUserDialog(false)}
          onSave={handleAddUser}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenGiftDialog(true)}
          >
            Add New Gift
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...gifts]
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((gift) => (
                  <TableRow key={gift.id}>
                    <TableCell>{gift.title}</TableCell>
                    <TableCell>{gift.description}</TableCell>
                    <TableCell>{gift.totalQuantity}</TableCell>
                    <TableCell>
                      {gift.isActive ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditGift(gift)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          gift.id &&
                          setPendingDelete({
                            kind: "gift",
                            id: gift.id,
                            title: gift.title,
                          })
                        }
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <GiftDialog
          open={openGiftDialog}
          onClose={() => {
            setOpenGiftDialog(false);
            setNewGift({
              title: "",
              description: "",
              totalQuantity: 1,
              isActive: true,
            });
          }}
          onSave={handleAddGift}
          gift={newGift}
          title="Add New Gift"
        />

        <GiftDialog
          open={!!editingGift}
          onClose={() => setEditingGift(null)}
          onSave={handleUpdateGift}
          gift={editingGift || undefined}
          title="Edit Gift"
        />
      </TabPanel>

      <Dialog
        open={!!pendingDelete}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            closeDeleteDialog();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          {pendingDelete?.kind === "guest" && (
            <Typography variant="body2">
              Remove <strong>{pendingDelete.name}</strong> from group{" "}
              <strong>{pendingDelete.groupName}</strong>? This cannot be undone.
            </Typography>
          )}
          {pendingDelete?.kind === "user" && (
            <Typography variant="body2">
              Remove guest login <strong>{pendingDelete.username}</strong>? This
              will delete{" "}
              <strong>
                {pendingDelete.guestCount}{" "}
                {pendingDelete.guestCount === 1 ? "person" : "people"}
              </strong>{" "}
              on the RSVP list and all wishlist reservations for this account.
            </Typography>
          )}
          {pendingDelete?.kind === "gift" && (
            <Typography variant="body2">
              Delete wishlist item <strong>{pendingDelete.title}</strong>? This
              only works if nobody has reserved it yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteInProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteInProgress}
          >
            {deleteInProgress ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
